import React, { useState } from 'react';
import { X, Megaphone, ChevronDown, ChevronUp } from 'lucide-react';
import './AnnouncementsBanner.css';

interface Props {
  rawAnnouncements: string | null;
}

const SECTION_RE = /^(I{1,3}|IV|V|VI{0,3}|VII|VIII|IX|X)\.\s/;

export const AnnouncementsBanner: React.FC<Props> = ({ rawAnnouncements }) => {
  const [isDismissed, setIsDismissed] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  if (!rawAnnouncements || isDismissed) {
    return null;
  }

  // Split into sections: force a newline before each Roman-numeral heading
  const lines = rawAnnouncements
    .replace(/\s+(I{1,3}|IV|V|VI{0,3}|VII|VIII|IX|X)\.\s+(HON'BLE)/g, '\n$1. $2')
    .split('\n')
    .map(s => s.trim())
    .filter(l => l && l !== '// BY ORDER //');

  // First line is the notification number/title
  const titleLine = lines[0];

  // Group remaining lines into sections: each section = { heading, body }
  // Body lines within a section are joined into a single prose paragraph.
  type Section = { heading: string; body: string };
  const sections: Section[] = [];
  let current: Section | null = null;
  for (const line of lines.slice(1)) {
    if (SECTION_RE.test(line)) {
      if (current) sections.push(current);
      current = { heading: line, body: '' };
    } else if (current) {
      current.body = current.body ? `${current.body} ${line}` : line;
    }
  }
  if (current) sections.push(current);

  return (
    <div className="announcements-banner">
      <div
        className="announcements-banner__header"
        onClick={() => setIsExpanded(!isExpanded)}
        role="button"
        aria-expanded={isExpanded}
      >
        <Megaphone size={14} className="announcements-banner__icon" />
        <span className="announcements-banner__notif-id">{titleLine}</span>
        <span className="announcements-banner__label">Court Notification</span>
        <span className="announcements-banner__spacer" />
        {isExpanded ? <ChevronUp size={14} className="announcements-banner__chevron" /> : <ChevronDown size={14} className="announcements-banner__chevron" />}
        <button
          className="announcements-banner__close"
          onClick={e => { e.stopPropagation(); setIsDismissed(true); }}
          aria-label="Dismiss"
        >
          <X size={14} />
        </button>
      </div>

      {isExpanded && (
        <div className="announcements-banner__content">
          {sections.map((s, idx) => (
            <div key={idx} className="announcements-banner__section">
              <p className="announcements-banner__section-header">{s.heading}</p>
              {s.body && <p className="announcements-banner__body-line">{s.body}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
